import { ref, shallowRef } from 'vue'
import { apiFetch } from '@/utils/api'

const DEBOUNCE_MS = 300
const PER_PAGE = 8
let debounceTimer = null

export function useGlobalSearch() {
  const query = ref('')
  const loading = ref(false)
  const results = shallowRef({ movies: [], tvshows: [], shows: [] })

  function genresFromSubtypes(attrs) {
    const raw = attrs?.subtypes ?? attrs?.subtype
    if (Array.isArray(raw)) {
      return raw.map((s) => (typeof s === 'string' ? s : s?.name)).filter(Boolean)
    }
    if (raw && typeof raw === 'object' && raw.name) return [raw.name]
    return []
  }

  function normalizeMovie(item) {
    const attrs = item.attributes ?? item
    return {
      type: 'movie',
      id: item.id ?? attrs.id,
      title: attrs.title ?? item.title ?? '',
      year: attrs.year ?? item.year ?? null,
      picture: attrs.picture ?? item.picture ?? '',
      rate: attrs.rate != null ? Number(attrs.rate) : null,
      genres: genresFromSubtypes(attrs),
    }
  }

  function normalizeTvshow(item) {
    const attrs = item.attributes ?? item
    return {
      type: 'tvshow',
      id: item.id ?? attrs.id,
      title: attrs.title ?? item.title ?? '',
      year: attrs.year ?? item.year ?? null,
      picture: attrs.picture ?? item.picture ?? '',
      rate: attrs.rate != null ? Number(attrs.rate) : null,
      genres: genresFromSubtypes(attrs),
    }
  }

  function normalizeShow(item) {
    const attrs = item.attributes ?? item
    const place = item.relationships?.place?.data?.name ?? attrs.place ?? item.place ?? null
    const subtypeName = item.relationships?.subtype?.data?.name ?? attrs.subtype?.name ?? null
    const genres = genresFromSubtypes(attrs)
    if (subtypeName && !genres.includes(subtypeName)) genres.unshift(subtypeName)
    return {
      type: 'show',
      id: item.id ?? attrs.id,
      title: attrs.title ?? item.title ?? '',
      place,
      picture: attrs.picture ?? item.picture ?? attrs.imageUrl ?? item.imageUrl ?? '',
      rate: attrs.rate != null ? Number(attrs.rate) : null,
      genres,
    }
  }

  async function runSearch(q) {
    const term = (q || '').trim()
    if (term.length < 2) {
      results.value = { movies: [], tvshows: [], shows: [] }
      return
    }
    loading.value = true
    try {
      const params = new URLSearchParams({
        title: term,
        page: '1',
        perPage: String(PER_PAGE),
      })
      const [moviesRes, tvshowsRes, showsRes] = await Promise.all([
        apiFetch(`/api/v1/movies?${params}`),
        apiFetch(`/api/v1/tvshows?${params}`),
        apiFetch(`/api/v1/shows?${params}`),
      ])

      const movies = moviesRes.ok
        ? (await moviesRes.json())?.data ?? []
        : []
      const tvshows = tvshowsRes.ok
        ? (await tvshowsRes.json())?.data ?? []
        : []
      const shows = showsRes.ok
        ? (await showsRes.json())?.data ?? []
        : []

      results.value = {
        movies: Array.isArray(movies) ? movies.map(normalizeMovie) : [],
        tvshows: Array.isArray(tvshows) ? tvshows.map(normalizeTvshow) : [],
        shows: Array.isArray(shows) ? shows.map(normalizeShow) : [],
      }
    } catch (e) {
      console.error('Global search error:', e)
      results.value = { movies: [], tvshows: [], shows: [] }
    } finally {
      loading.value = false
    }
  }

  function search(q) {
    const value = q ?? ''
    query.value = value
    if (debounceTimer) clearTimeout(debounceTimer)
    if (!value.trim()) {
      results.value = { movies: [], tvshows: [], shows: [] }
      return
    }
    debounceTimer = setTimeout(() => runSearch(value), DEBOUNCE_MS)
  }

  function clear() {
    query.value = ''
    results.value = { movies: [], tvshows: [], shows: [] }
    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
  }

  const hasResults = () =>
    results.value.movies.length > 0 ||
    results.value.tvshows.length > 0 ||
    results.value.shows.length > 0

  return { query, loading, results, search, clear, hasResults }
}
